import { ModelLookupContext } from "@auer-steel/Contexts/ModelLookup/ModelLookupContext";
import ModelSearchListDetail from "@auer-steel/CustomComponents/ModelLookup/ModelSearchListDetail";
import ModelSearchResultsList from "@auer-steel/CustomComponents/ModelLookup/ModelSearchResultsList";
import ModelLookup from "@auer-steel/CustomTypes/ModelLookup";
import ModelPartsLookup from "@auer-steel/CustomTypes/ModelPartsLookup";
import {
    getModelLookupByModelNumber,
    getModelPartsLookupByModelNumber,
    getModelProductByProductNumber,
} from "@auer-steel/Services/ModelLookupService";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import translate from "@insite/client-framework/Translate";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonProps } from "@insite/mobius/Button";
import { OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ModelSearchStyles {
    loadingSpinner?: LoadingSpinnerProps;
    gridContainer?: GridContainerProps;
    searchForm_GridItem?: GridItemProps;
    partFinder_title__GridItem?: GridItemProps;
    title_Typography?: InjectableCss;
    searchButtonGridItem?: GridItemProps;
    currentProductGridItem?: GridItemProps;
    currentProductAreaContainer?: GridContainerProps;
    searchForm_GridItem__right?: GridItemProps;
}

export const styles: ModelSearchStyles = {
    gridContainer: {
        css: css`
            min-height: 600px;
        `,
    },
    searchForm_GridItem: {
        css: css`
            display: flex;
            padding: 5px 0px;
            flex-wrap: wrap;
        `,
    },
    title_Typography: {
        css: css`
            font-size: 32px;
            font-weight: 600;
            margin-bottom: 1rem;
            display: block;
        `,
    },
    searchButtonGridItem: {
        width: 3,
        align: 'bottom',
    },
    searchForm_GridItem__right: {
        css: css`
            justify-content:flex-end;
        `,
    },
    currentProductGridItem: {
        width: [12, 12, 4, 4, 4],
    },
    currentProductAreaContainer: {
        gap: 10,
    },
    loadingSpinner: {
        size: 15,
        css: css`
            margin-top: auto;
            margin-bottom: auto;
            margin-left: auto;
        `,
    },
};

type Props = HasHistory;

const ModelSearch: React.FC<Props> = ({ history }) => {
    const modelLookupContext = React.useContext(ModelLookupContext);
    const {
        modelNumber,
        modelPartsLookupResultsData,
        displayDetailsView,
        modelLookupRootUri,
        handleSetIsLoading,
        handleSetModelNumber,
        handleSetModelLookupResultsData,
        handleSetModelPartsLookupResultsData,
        handleSetCurrentSelectedProduct,
        handleSetDisplayDetailsView,
        handleSetModelLookupRootUri,
    } = modelLookupContext;

    const [options, setOptions] = useState<OptionObject[]>([]);
    const [selectedPartNumber, setSelectedPartNumber] = useState<string>("");

    const getModelNumberFromQueryString = () => {
        if (typeof window !== "undefined" && modelLookupRootUri.length < 1) {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has("modelDetail")) {
                const queryStringModelNumber = urlParams.get("modelDetail")!;
                handleSetModelNumber(queryStringModelNumber);
            }

            handleSetModelLookupRootUri(window.location.href);
        }
    };

    const handleInputChange: TextFieldProps["onChange"] = event => {
        const { target } = event;
        const modelNumberSearchTerms = target.value;
        handleSetModelNumber(modelNumberSearchTerms);
    };

    const submitModelSearchHandler = async () => {
        try {
            const optionsItems: OptionObject[] = [];
            const data: ModelLookup = {} as ModelLookup;
            handleSetModelLookupResultsData(data);

            if (!modelNumber) {
                getModelNumberFromQueryString();
                return;
            }

            handleSetDisplayDetailsView(false);
            handleSetIsLoading(true);

            const results: [ModelPartsLookup] = await getModelPartsLookupByModelNumber(modelNumber.toUpperCase());
            const currentSelectedProduct: ProductDto = await getModelProductByProductNumber(modelNumber.toUpperCase());
            handleSetIsLoading(false);
            handleSetModelPartsLookupResultsData(results);
            handleSetCurrentSelectedProduct(currentSelectedProduct);

            Object.entries(results[0].packageLines).map(([key, value]) => {
                optionsItems.push({ optionText: value.salesPackage, optionValue: value.salesPackage });
            });

            setOptions(optionsItems);

            if (results[0].packageLines.length === 1) {
                handleSetModelItemsForPartNumber(results[0].modelNumber);
            }
        } catch (ex) {
            handleSetIsLoading(false);
            logger.debug(ex);
        }
    };

    const handleSetModelItems = async function (event: React.FormEvent<HTMLSelectElement>) {
        await handleSetModelItemsForPartNumber((event.target as HTMLSelectElement).value);
    };

    const handleSetModelItemsForPartNumber = async (part: string) => {
        setSelectedPartNumber(part);

        try {
            if (!modelNumber) {
                return;
            }

            handleSetDisplayDetailsView(false);
            handleSetIsLoading(true);
            const results: ModelLookup = await getModelLookupByModelNumber(part);
            handleSetIsLoading(false);
            handleSetModelLookupResultsData(results);
        } catch (ex) {
            handleSetIsLoading(false);
            logger.debug(ex);
        }
    };

    const handleKeypress = (e: { keyCode: number }) => {
        if (e.keyCode === 13) {
            submitModelSearchHandler();
        }
    };

    React.useEffect(() => getModelNumberFromQueryString(), []);


    return (
        <GridContainer className="partFinder" gap={10} {...styles.gridContainer}>
            <GridItem width={12}>
                <Typography className="partFinder_title" {...styles.title_Typography}>{translate("Part Finder")}</Typography>
            </GridItem>
            <GridItem className="partFinder_searchForm" width={12} align="bottom" {...styles.searchForm_GridItem}>
                <GridItem className="partFinder_modelNumber" width={[12, 12, 12, 6, 6]} align="bottom">
                    <GridContainer gap={10}>
                        <GridItem width={12}>
                            <Typography
                                className="partFinder_modelNumberLabel"
                                weight="bold"
                                as="label"
                                htmlFor="modelNumberTextField"
                            >
                                {translate("Search by model # to find the parts you need")}
                            </Typography>
                        </GridItem>
                        <GridItem width={9}>
                            <TextField
                                className="partFinder_modelNumberField"
                                id="modelNumberTextField"
                                name="ModelNumberTextField"
                                onChange={handleInputChange}
                                onKeyDown={handleKeypress}
                            />
                        </GridItem>
                        <GridItem
                            className="partFinder_modelNumberSubmit"
                            align="bottom"
                            width={3}
                        >
                            {/* NOTE: Would like to get the width of the modelNumberField
                                to be all of the 12 except the size of the button.
                                css={css`justify-content:flex-end;`}> */}
                            <Button type="submit" onClick={submitModelSearchHandler}>
                                {translate("Search")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {modelPartsLookupResultsData[0]?.packageLines && (
                    <GridItem 
                        className="partFinder_partNumber"
                        width={[12, 12, 12, 6, 6]}
                        align="bottom"
                        {...styles.searchForm_GridItem}
                        {...styles.searchForm_GridItem__right}
                    >
                        <Select
                            label={translate("Choose a Part Number")}
                            data-test-selector="selectPartNumber"
                            options={options.map(o => o.optionText)}
                            onChange={(event: React.FormEvent<HTMLSelectElement>) => handleSetModelItems(event)}
                        >
                            <option
                                value={
                                    modelPartsLookupResultsData[0].packageLines.length === 1
                                        ? modelPartsLookupResultsData[0].modelNumber
                                        : ""
                                }
                            >
                                {modelPartsLookupResultsData[0].packageLines.length === 1
                                    ? modelPartsLookupResultsData[0].modelNumber
                                    : translate("Select")}
                            </option>
                            {options.map(o => (
                                <option
                                    key={o.optionText}
                                    value={o.optionText}
                                >
                                    {o.optionText}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                )}
            </GridItem>
            <>
                <ModelSearchResultsList />
                {displayDetailsView && (
                    <ModelSearchListDetail />
                )}
            </>
        </GridContainer >
    );
};

export default connect()(withHistory(ModelSearch));
